import { createCopyOfTableContents } from "../../../services/TableFunctions";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import {
    COMPONENTS_KEY,
    CONTRIBUTORS_KEY,
    EDITABLE_DATA_FIELDS_CONFIGURATION_GRID,
    EDITABLE_FIELDS,
    EDITABLE_FIELDS_GRID,
    IP_NAMES_SECTION_STATE_KEY,
    IS_HIDDEN_FIELD,
    MATCHING_REPERTOIRE_WORKS_KEY,
    ORDER_GRID_FIELD_METADATA,
    PRODUCT_WORKS_KEY,
    READ_ONLY_INPUT,
    SEQUENCE_FIELD,
    TEXT_INPUT,
    TIME_INPUT,
    USAGE_DETAILS_ROWS_SECTION_STATE_KEY,
    USAGE_MAINTENANCE_GRIDS_VIEW_COMMENTS_STATE_KEY,
    USAGE_ROWS_SECTION_KEY,
    WORK_CONTRIBUTOR_RELATIONSHIPS_STATE_KEY,
} from "../../../Consts";
import { IUsagePoolDatesAndDimensions } from "../../../types/usageTypes/IUsagePoolDatesAndDimensions";

type ChangeData = (
    value: any,
    name: IRepertoireStateKeys,
    objectKey?: string,
    ipRepresentationsIdsToRemove?: number[],
) => void;

type UpdateContributorRelationships = (
    value: string,
    contributors: any[],
    rowIndex: number,
) => void;

type ToggleHeaderCheckbox = (fieldName: string, isChecked: boolean) => void;

export interface IChangeTableDataParams {
    value: any;
    stateKey: IRepertoireStateKeys;
    objectKey: string;
    rowIndex: number;
    tableContents: any[];
    changeData: ChangeData;
    componentInstance?: string;
    newOrderValue?: string;
    updateContributorRelationships?: UpdateContributorRelationships;
    datesAndDimensions?: IUsagePoolDatesAndDimensions[];
    checkableColumns?: string[];
    toggleHeaderCheckbox?: ToggleHeaderCheckbox;
}

interface IResolvedChangeResult {
    tableContentsCopy: any[];
    resolvedStateKey: IRepertoireStateKeys;
    shouldReturnEarly: boolean;
}

const handleMatchingRepertoireWorksDefiniteChange = (
    tableContentsCopy: any[],
    rowIndex: number,
    objectKey: string,
    value: any,
): void => {
    if (tableContentsCopy.length > 1) {
        tableContentsCopy[rowIndex][objectKey].value = value;

        for (let index = 0; index < tableContentsCopy.length; index++) {
            if (index !== rowIndex) {
                tableContentsCopy[index][objectKey].value = false;
            }
        }
        return;
    }
    tableContentsCopy[rowIndex][objectKey].value = value;
};

const handleEditableDataFieldsConfigurationChange = (
    tableContentsCopy: any[],
    rowIndex: number,
    objectKey: string,
    value: any
): void => {
    if (objectKey === IS_HIDDEN_FIELD) {
        tableContentsCopy[rowIndex].hidden = !tableContentsCopy[rowIndex].hidden;
    } else if (objectKey === "defaultValue") {
        tableContentsCopy[rowIndex].defaultValue = value;
    } else if (objectKey === "isMandatory") {
        tableContentsCopy[rowIndex].isMandatory = value;
    } else if (objectKey === "isInTheBody") {
        tableContentsCopy[rowIndex].isInTheBody = value;
    } else {
        tableContentsCopy[rowIndex].data = value;
    }

    tableContentsCopy[rowIndex].modifiedByUser = true;
};

const handleEditableFieldsChange = (
    tableContentsCopy: any[],
    rowIndex: number,
    objectKey: string,
    value: any
): void => {
    if (objectKey === IS_HIDDEN_FIELD) {
        tableContentsCopy[rowIndex].hidden = !tableContentsCopy[rowIndex].hidden;
    } else if (objectKey === "defaultValue") {
        tableContentsCopy[rowIndex].defaultValue = value;
    } else if (objectKey === "isMandatory") {
        tableContentsCopy[rowIndex].isMandatory = value;
    } else {
        tableContentsCopy[rowIndex].data = value;
    }

    tableContentsCopy[rowIndex].modifiedByUser = true;
};

const handleEditableFieldsGridChange = (
    tableContentsCopy: any[],
    tableContents: any[],
    rowIndex: number,
    objectKey: string,
    value: any,
    newOrderValue?: string
): void => {
    if (objectKey === IS_HIDDEN_FIELD) {
        tableContentsCopy[rowIndex].hidden = !tableContentsCopy[rowIndex].hidden;
        tableContentsCopy[rowIndex].modifiedByUser = true;
        return;
    }

    if (objectKey === SEQUENCE_FIELD) {
        const orders = tableContents.map(item => item[ORDER_GRID_FIELD_METADATA]);
        const newOrder = parseInt(newOrderValue, 10);

        if (newOrder > rowIndex) {
            tableContentsCopy[rowIndex][ORDER_GRID_FIELD_METADATA] = orders[newOrder];

            for (let index = rowIndex; index < newOrder; index++) {
                tableContentsCopy[index + 1][ORDER_GRID_FIELD_METADATA] = orders[index];
            }
        } else if (newOrder < rowIndex) {
            tableContentsCopy[rowIndex][ORDER_GRID_FIELD_METADATA] = orders[newOrder];

            for (let index = newOrder; index < rowIndex; index++) {
                tableContentsCopy[index][ORDER_GRID_FIELD_METADATA] = orders[index + 1];
            }
        }

        tableContentsCopy[rowIndex].modifiedByUser = true;
        return;
    }

    tableContentsCopy[rowIndex].data = value;
};

const handleEffectiveDateRangeChange = (
    tableContentsCopy: any[],
    rowIndex: number,
    value: any,
    datesAndDimensions?: IUsagePoolDatesAndDimensions[]
): void => {
    if (!datesAndDimensions?.length) {
        return;
    }

    tableContentsCopy[rowIndex].effectiveDateRange.value = value;

    const result = datesAndDimensions.find(
        item => item.effectiveDateRange === value
    );

    if (!result) {
        return;
    }

    tableContentsCopy[rowIndex].dimensionsAndDatesID = result.poolDimensionsAndDatesID;
    tableContentsCopy[rowIndex].use.inputType = result.use ? TEXT_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].usageDimensionOne.inputType = result.dimension1 ? TEXT_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].usageDimensionTwo.inputType = result.dimension2 ? TEXT_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].usageDimensionThree.inputType = result.dimension3 ? TEXT_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].usageDimensionFour.inputType = result.dimension4 ? TEXT_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].usageDimensionFive.inputType = result.dimension5 ? TEXT_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].fromTime.inputType = result.time ? TIME_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].toTime.inputType = result.time ? TIME_INPUT : READ_ONLY_INPUT;
    tableContentsCopy[rowIndex].musicSource.inputType = result.musicSource ? TEXT_INPUT : READ_ONLY_INPUT;

    tableContentsCopy[rowIndex].use.value = result.use ? tableContentsCopy[rowIndex].use.value : "";
    tableContentsCopy[rowIndex].usageDimensionOne.value = result.dimension1 ? tableContentsCopy[rowIndex].usageDimensionOne.value : "";
    tableContentsCopy[rowIndex].usageDimensionTwo.value = result.dimension2 ? tableContentsCopy[rowIndex].usageDimensionTwo.value : "";
    tableContentsCopy[rowIndex].usageDimensionThree.value = result.dimension3 ? tableContentsCopy[rowIndex].usageDimensionThree.value : "";
    tableContentsCopy[rowIndex].usageDimensionFour.value = result.dimension4 ? tableContentsCopy[rowIndex].usageDimensionFour.value : "";
    tableContentsCopy[rowIndex].usageDimensionFive.value = result.dimension5 ? tableContentsCopy[rowIndex].usageDimensionFive.value : "";
    tableContentsCopy[rowIndex].fromTime.value = result.time ? tableContentsCopy[rowIndex].fromTime.value : "";
    tableContentsCopy[rowIndex].toTime.value = result.time ? tableContentsCopy[rowIndex].toTime.value : "";
    tableContentsCopy[rowIndex].musicSource.value = result.musicSource ? tableContentsCopy[rowIndex].musicSource.value : "";
};

const updateCheckableColumnState = (
    checkableColumns: string[],
    objectKey: string,
    value: any,
    tableContents: any[],
    toggleHeaderCheckbox?: ToggleHeaderCheckbox
): void => {
    if (!checkableColumns.includes(objectKey) || !toggleHeaderCheckbox) {
        return;
    }

    const allChecked =
        value &&
        tableContents?.length > 0 &&
        tableContents.every(item => item[objectKey]?.value);

    toggleHeaderCheckbox(objectKey, allChecked);
};

const applyTableDataChange = (
    params: IChangeTableDataParams
): IResolvedChangeResult => {
    const {
        value,
        stateKey,
        objectKey,
        rowIndex,
        tableContents,
        componentInstance,
        newOrderValue,
        updateContributorRelationships,
    } = params;

    let resolvedStateKey = stateKey;

    const shouldDeepCopy =
        componentInstance === undefined || componentInstance.length > 0;

    const tableContentsCopy = createCopyOfTableContents(
        tableContents,
        shouldDeepCopy
    );

    if (
        stateKey === MATCHING_REPERTOIRE_WORKS_KEY &&
        objectKey === "isDefinite"
    ) {
        handleMatchingRepertoireWorksDefiniteChange(
            tableContentsCopy,
            rowIndex,
            objectKey,
            value
        );

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (
        stateKey === CONTRIBUTORS_KEY &&
        objectKey === WORK_CONTRIBUTOR_RELATIONSHIPS_STATE_KEY
    ) {
        updateContributorRelationships?.(value, tableContentsCopy, rowIndex);

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: true,
        };
    }

    if (objectKey === "workName") {
        tableContentsCopy[rowIndex][objectKey].value = value;
        tableContentsCopy[rowIndex].workNumber.value =
            tableContentsCopy[rowIndex].productWorks[0].number;
        tableContentsCopy[rowIndex].workId =
            tableContentsCopy[rowIndex].productWorks[0].workCode;

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (
        stateKey === CONTRIBUTORS_KEY &&
        (objectKey === "relationships" || objectKey === "societyOfLicense")
    ) {
        tableContentsCopy[rowIndex][objectKey].value = value;

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (componentInstance === EDITABLE_DATA_FIELDS_CONFIGURATION_GRID) {
        resolvedStateKey = componentInstance as IRepertoireStateKeys;

        handleEditableDataFieldsConfigurationChange(
            tableContentsCopy,
            rowIndex,
            objectKey,
            value
        );

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (componentInstance === EDITABLE_FIELDS) {
        resolvedStateKey = componentInstance as IRepertoireStateKeys;

        handleEditableFieldsChange(
            tableContentsCopy,
            rowIndex,
            objectKey,
            value
        );

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (componentInstance === EDITABLE_FIELDS_GRID) {
        resolvedStateKey = componentInstance as IRepertoireStateKeys;

        handleEditableFieldsGridChange(
            tableContentsCopy,
            tableContents,
            rowIndex,
            objectKey,
            value,
            newOrderValue
        );

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (
        componentInstance === COMPONENTS_KEY ||
        componentInstance === PRODUCT_WORKS_KEY
    ) {
        resolvedStateKey = componentInstance as IRepertoireStateKeys;
        tableContentsCopy[rowIndex][objectKey].value = value;

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (
        stateKey === USAGE_ROWS_SECTION_KEY ||
        stateKey === USAGE_DETAILS_ROWS_SECTION_STATE_KEY ||
        stateKey === IP_NAMES_SECTION_STATE_KEY
    ) {
        tableContentsCopy[rowIndex][objectKey] = value;

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    if (
        componentInstance === "comments" ||
        componentInstance === USAGE_MAINTENANCE_GRIDS_VIEW_COMMENTS_STATE_KEY
    ) {
        tableContentsCopy[rowIndex][objectKey].value = value;
        tableContentsCopy[rowIndex].isModified = true;

        return {
            tableContentsCopy,
            resolvedStateKey,
            shouldReturnEarly: false,
        };
    }

    tableContentsCopy[rowIndex][objectKey].value = value;

    return {
        tableContentsCopy,
        resolvedStateKey,
        shouldReturnEarly: false,
    };
};

/**
 * Applies DataGridTable field update rules and emits the updated table contents.
 */
export const changeTableData = (params: IChangeTableDataParams): void => {
    const {
        value,
        objectKey,
        rowIndex,
        changeData,
        datesAndDimensions,
        checkableColumns = [],
        toggleHeaderCheckbox,
        tableContents,
    } = params;

    const {
        tableContentsCopy,
        resolvedStateKey,
        shouldReturnEarly,
    } = applyTableDataChange(params);

    if (shouldReturnEarly) {
        return;
    }

    if (objectKey === "effectiveDateRange") {
        handleEffectiveDateRangeChange(
            tableContentsCopy,
            rowIndex,
            value,
            datesAndDimensions
        );
    }

    updateCheckableColumnState(
        checkableColumns,
        objectKey,
        value,
        tableContents,
        toggleHeaderCheckbox
    );

    changeData(tableContentsCopy, resolvedStateKey, objectKey);
};